import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { megaMenuCategories } from '../../data/megaMenuData';

/**
 * Desktop-only dropdown mega menu rendered when the Products nav item is active.
 * Positioned as a fixed panel below the navbar.
 */
const DesktopMegaMenu = ({
  onClose,
  onMouseLeave,
  expandedSubcategories,
  onToggleSubcategory,
}) => {
  return (
    <div
      className="fixed left-0 right-0 top-[88px] z-50 flex justify-center px-4"
      onMouseLeave={onMouseLeave}
    >
      <div
        className="w-full max-w-4xl bg-white/95 backdrop-blur-2xl border-2 border-cyan-500/30 rounded-2xl shadow-2xl shadow-black/10"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.98)' }}
      >
        <div className="p-8">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between border-b border-cyan-500/20 pb-4">
            <h3 className="text-xl font-bold text-gray-800">Our Products</h3>
            <Link
              to="/products"
              onClick={onClose}
              className="inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 transition-all shadow-md"
            >
              View All Products →
            </Link>
          </div>

          {/* Mega Menu Grid */}
          <div className="grid grid-cols-3 gap-8">
            {megaMenuCategories.map((category) => (
              <MegaMenuColumn
                key={category.name}
                category={category}
                onClose={onClose}
                expandedSubcategories={expandedSubcategories}
                onToggleSubcategory={onToggleSubcategory}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MegaMenuColumn = ({ category, onClose, expandedSubcategories, onToggleSubcategory }) => {
  const hasPath = category.path && category.path !== '#';
  return (
    <div className="space-y-4">
      {/* Category Header */}
      {hasPath ? (
        <Link
          to={category.path}
          onClick={onClose}
          className="block text-base font-bold text-gray-800 hover:text-cyan-600 transition-colors border-b-2 border-cyan-500/30 pb-2.5"
        >
          {category.name}
        </Link>
      ) : (
        <div className="block text-base font-bold text-gray-800 border-b-2 border-cyan-500/30 pb-2.5">
          {category.name}
        </div>
      )}

      {/* Subcategories & Products */}
      <div className="space-y-4">
        {category.subcategories && category.subcategories.map((subcat) => (
          <MegaMenuSubcategory
            key={subcat.name}
            subcat={subcat}
            onClose={onClose}
            isExpanded={!!expandedSubcategories[subcat.name]}
            onToggle={() => onToggleSubcategory(subcat.name)}
          />
        ))}
      </div>
    </div>
  );
};

const MegaMenuSubcategory = ({ subcat, onClose, isExpanded, onToggle }) => {
  const hasProducts = subcat.products && subcat.products.length > 0;

  if (!hasProducts) {
    return (
      <div className="space-y-2">
        <Link
          to={subcat.path}
          onClick={onClose}
          className="block text-sm font-semibold text-cyan-600 hover:text-cyan-700 transition-colors"
        >
          {subcat.name}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        onClick={onToggle}
        className="flex items-center justify-between px-3 py-1.5 rounded-lg cursor-pointer hover:bg-cyan-50 transition-colors group"
      >
        <Link
          to={subcat.path}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="text-sm font-semibold text-cyan-600 hover:text-cyan-700 transition-colors"
        >
          {subcat.name}
        </Link>
        <ChevronDown
          className={`h-4 w-4 text-cyan-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </div>

      {isExpanded && (
        <div className="ml-3 mt-1.5 space-y-1.5 border-l-2 border-cyan-500/20 pl-3">
          {subcat.products.map((product) => (
            <Link
              key={product.path}
              to={product.path}
              onClick={onClose}
              className="block text-xs text-gray-600 hover:text-cyan-600 hover:translate-x-1 transition-all leading-relaxed"
            >
              • {product.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesktopMegaMenu;
